import { Typography } from '@mui/material'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import MuiTooltip from '@mui/material/Tooltip'
import { Box, useMediaQuery } from '@mui/system'
import PropTypes from 'prop-types'
import React from 'react'

import { TOOLTIP_POSITION } from './tooltip.constants'
import { styles } from './tooltip.styles'

const Tooltip = ({
  children,
  title,
  placement,
  arrow,
  customStyle,
  open: controlledOpen,
  onOpen: controlledOnOpen,
  onClose: controlledOnClose,
  trigger = 'hover',
  disableMobileCloseOnScroll = false,
  ...tooltipProps
}) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery((theme) => theme.breakpoints.down('md'))

  const isControlled = typeof controlledOpen === 'boolean'
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false)
  const [isLocked, setIsLocked] = React.useState(false)
  const derivedOpen = isControlled ? controlledOpen : uncontrolledOpen
  const open = isControlled ? derivedOpen : isLocked || derivedOpen

  const handleTooltipClose = (_event, reason) => {
    if (trigger === 'click' && isLocked) {
      if (reason === 'clickAway' || reason === 'manual') {
        setIsLocked(false)
      } else {
        return
      }
    }

    if (isControlled) {
      controlledOnClose?.()
    } else {
      setUncontrolledOpen(false)
    }
  }

  const handleTooltipOpen = (e, { lock } = { lock: false }) => {
    e?.stopPropagation?.()
    if (isControlled) {
      controlledOnOpen?.()
    } else {
      setUncontrolledOpen(true)
    }
    if (trigger === 'click') {
      setIsLocked(lock)
    }
  }

  const childrenWithOnClick = React.cloneElement(children, {
    onClick: (e) => {
      children.props.onClick?.(e)
      if (trigger === 'click') {
        if (isLocked) {
          handleTooltipClose(e, 'manual')
        } else {
          handleTooltipOpen(e, { lock: true })
        }
      }
    },
  })

  const renderTitle = () => (
    <Typography typography="header5" sx={customStyle}>
      {title}
    </Typography>
  )

  const tooltipBaseProps = {
    title: renderTitle(),
    placement: isTablet ? placement : 'bottom',
    variant: 'header5',
    arrow,
    slotProps: {
      popper: {
        disablePortal: false,
      },
    },
    ...tooltipProps,
  }

  const disableHoverWhenLocked = trigger === 'click' && isLocked

  const tooltipContent = (
    <MuiTooltip
      onClose={handleTooltipClose}
      onOpen={handleTooltipOpen}
      open={open}
      disableHoverListener={disableHoverWhenLocked}
      disableTouchListener={
        disableHoverWhenLocked || (trigger !== 'hover' && (isMobile || isTablet))
      }
      disableFocusListener={
        disableHoverWhenLocked || (trigger !== 'hover' && (isMobile || isTablet))
      }
      {...tooltipBaseProps}
    >
      {trigger === 'click' ? childrenWithOnClick : children}
    </MuiTooltip>
  )

  const shouldUseClickAway = trigger === 'click' || isMobile || isTablet

  return shouldUseClickAway ? (
    <ClickAwayListener onClickAway={(event) => handleTooltipClose(event, 'clickAway')}>
      <Box sx={isMobile || isTablet ? undefined : styles.base}>{tooltipContent}</Box>
    </ClickAwayListener>
  ) : (
    <Box sx={styles.base}>{tooltipContent}</Box>
  )
}

Tooltip.propTypes = {
  title: PropTypes.string.isRequired,
  placement: PropTypes.oneOf(Object.values(TOOLTIP_POSITION)),
  children: PropTypes.any,
  arrow: PropTypes.bool,
  customStyle: PropTypes.any,
  open: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  trigger: PropTypes.oneOf(['hover', 'click']),
}

Tooltip.defaultProps = {
  placement: TOOLTIP_POSITION.topStart,
  title: 'title',
  arrow: false,
  trigger: 'hover',
}

export default Tooltip
